import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useOutletContext } from 'react-router-dom';
import api from '../../services/api';
import NoClientState from './NoClientState';
import './ai-vis.css';

const PAGE_SIZES = [20, 40, 50, 100];

/**
 * Format Cloro model slugs into readable labels.
 *   gpt-5-5      → GPT 5.5
 *   gpt-5-3-mini → GPT 5.3 mini
 */
function formatModelName(raw) {
  if (!raw) return '';
  const lower = raw.toLowerCase().trim();
  if (lower.startsWith('gpt-')) {
    const parts = lower.slice(4).split('-');
    const numericParts = [];
    const suffixParts  = [];
    let inSuffix = false;
    for (const p of parts) {
      if (!inSuffix && /^\d+$/.test(p)) {
        numericParts.push(p);
      } else {
        inSuffix = true;
        suffixParts.push(p);
      }
    }
    const version = numericParts.join('.');
    const suffix  = suffixParts.join(' ');
    const label   = [version, suffix].filter(Boolean).join(' ');
    return `GPT ${label}`.trim();
  }
  return raw;
}

/* ── Sort icon ───────────────────────────────────────────────────────────── */
function SortIcon({ active, dir }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden
      className={`aivis-comp-sort-icon${active ? ' aivis-comp-sort-icon--active' : ''}`}
    >
      <path d="M3.5 5L6 2L8.5 5H3.5Z" fill="currentColor" opacity={active && dir === 'asc' ? 1 : 0.35} />
      <path d="M3.5 7L6 10L8.5 7H3.5Z" fill="currentColor" opacity={active && dir === 'desc' ? 1 : 0.35} />
    </svg>
  );
}

function SortHeader({ label, sortKey, currentSort, currentDir, onSort }) {
  const active = currentSort === sortKey;
  return (
    <th
      className="aivis-comp-th-sortable"
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (currentDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <span className="aivis-comp-th-inner">
        {label}
        <SortIcon active={active} dir={currentDir} />
      </span>
    </th>
  );
}

/* ── Chevron icon ────────────────────────────────────────────────────────── */
function Chevron({ open }) {
  return (
    <svg
      className={`aivis-foq-chevron${open ? ' aivis-foq-chevron--open' : ''}`}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ── Frequency bar cell ──────────────────────────────────────────────────── */
function FrequencyCell({ value, maxValue }) {
  const showBar = maxValue > 1;
  const pct = showBar && maxValue > 0 ? Math.round((value / maxValue) * 100) : 0;
  return (
    <div className="aivis-foq-freq-cell">
      <span className="aivis-foq-freq-num">{value.toLocaleString()}</span>
      {showBar && (
        <div className="aivis-foq-freq-bar-wrap">
          <div className="aivis-foq-freq-bar" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

/* ── Skeleton rows ───────────────────────────────────────────────────────── */
function SkeletonRows({ count = 8 }) {
  return Array.from({ length: count }).map((_, i) => (
    <tr key={i} className="aivis-foq-skeleton-row">
      <td><div className="aivis-skeleton" style={{ width: '70%' }} /></td>
      <td><div className="aivis-skeleton" style={{ width: '45%' }} /></td>
      <td>
        <div className="aivis-foq-freq-cell">
          <div className="aivis-skeleton" style={{ width: 36 }} />
          <div className="aivis-skeleton aivis-foq-freq-bar-wrap" />
        </div>
      </td>
    </tr>
  ));
}

/* ── CSV export (one row per unique query — Query + Frequency + Model) ───── */
function exportCsv(grouped, clientName) {
  const headers = ['Fan-Out Query', 'Frequency', 'Model'];
  const lines = grouped.map((g) => [
    `"${(g.query || '').replace(/"/g, '""')}"`,
    g.totalFrequency,
    `"${(g.models || []).map(formatModelName).join(', ').replace(/"/g, '""')}"`,
  ].join(','));
  const csv = [headers.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  const safeName = (clientName || 'Client').replace(/[^a-zA-Z0-9_-]/g, '_');
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName}_FanOut_Queries_${dd}${mm}${yyyy}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Pagination controls ─────────────────────────────────────────────────── */
function PaginationBar({ page, totalPages, pageSize, total, onPage, onPageSize }) {
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="aivis-foq-pagination">
      <span className="aivis-foq-pagination-info">
        {total === 0 ? 'No results' : `Showing ${start}–${end} of ${total} queries`}
      </span>
      <div className="aivis-foq-pagination-controls">
        <label className="aivis-foq-pagesize-label">
          Rows per page
          <select
            className="aivis-foq-pagesize"
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <span className="aivis-foq-page-indicator">
          Page {page} of {Math.max(totalPages, 1)}
        </span>
        <button
          type="button"
          className="aivis-foq-page-btn"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          className="aivis-foq-page-btn"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
export default function FanOutQueriesPage() {
  const { selectedCampaignId, campaigns } = useOutletContext();
  const location = useLocation();
  const clientName = useMemo(
    () => campaigns?.find((c) => String(c.id) === String(selectedCampaignId))?.client_name || '',
    [campaigns, selectedCampaignId],
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // search / sort — seed search from router state when navigating from Responses page
  const [search, setSearch] = useState(() => location.state?.promptSearch || '');
  const [sortKey, setSortKey] = useState('totalFrequency');
  const [sortDir, setSortDir] = useState('desc');

  // accordion
  const [expandedQueries, setExpandedQueries] = useState(new Set());

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  function handleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  }

  function toggleExpand(query) {
    setExpandedQueries((prev) => {
      const next = new Set(prev);
      if (next.has(query)) next.delete(query);
      else next.add(query);
      return next;
    });
  }

  const load = useCallback(async () => {
    if (!selectedCampaignId) { setRows([]); return; }
    setLoading(true); setError('');
    try {
      const { data } = await api.get(`/aivis/campaigns/${selectedCampaignId}/fan-out-queries/`);
      setRows(Array.isArray(data) ? data : (data.results || []));
    } catch (ex) {
      setError(ex.response?.data?.detail || 'Could not load fan-out queries.');
    } finally {
      setLoading(false);
    }
  }, [selectedCampaignId]);

  useEffect(() => { load(); }, [load]);

  // Reset page whenever search / sort / pageSize / campaign changes
  useEffect(() => { setPage(1); }, [search, sortKey, sortDir, pageSize, selectedCampaignId]);

  // ── 1. Group by query text ─────────────────────────────────────────────
  const grouped = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.query)) {
        map.set(r.query, { query: r.query, totalFrequency: 0, prompts: [], models: [] });
      }
      const g = map.get(r.query);
      g.totalFrequency += r.frequency;
      g.prompts.push({ prompt_id: r.prompt_id, prompt_text: r.prompt_text, frequency: r.frequency });
      (r.models || []).forEach((m) => { if (m && !g.models.includes(m)) g.models.push(m); });
    });
    // Sort each prompt list by frequency desc
    map.forEach((g) => g.prompts.sort((a, b) => b.frequency - a.frequency));
    return Array.from(map.values());
  }, [rows]);

  // ── 2. Filter ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return grouped;
    return grouped.filter(
      (g) =>
        g.query.toLowerCase().includes(q) ||
        g.prompts.some((p) => (p.prompt_text || '').toLowerCase().includes(q)),
    );
  }, [grouped, search]);

  // ── 3. Sort ───────────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? (sortDir === 'asc' ? Infinity : -Infinity);
      const bv = b[sortKey] ?? (sortDir === 'asc' ? Infinity : -Infinity);
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return sortDir === 'asc' ? -1 : 1;
      if (as > bs) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  // ── 4. Paginate ───────────────────────────────────────────────────────
  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const maxFreq = useMemo(
    () => Math.max(...sorted.map((g) => g.totalFrequency), 0),
    [sorted],
  );

  const anyExpanded = expandedQueries.size > 0;

  if (!selectedCampaignId) {
    return <NoClientState />;
  }

  return (
    <div className="aivis-comp-page">
      <header className="aivis-dash-header">
        <div>
          <h1 className="aivis-page-title">Fan-Out Queries</h1>
          <p className="aivis-page-sub">
            Internal search queries ChatGPT uses to find sources while answering your prompts.
            Aggregated by frequency across all executions.
          </p>
        </div>
      </header>

      {/* Toolbar: search + count + export */}
      <div className="aivis-foq-toolbar">
        <div className="aivis-foq-search-wrap">
          <svg className="aivis-foq-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            className="aivis-foq-search"
            placeholder="Search queries or prompts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="aivis-foq-search-clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
        {!loading && grouped.length > 0 && (
          <span className="aivis-foq-count">
            {filtered.length !== grouped.length
              ? `${filtered.length} of ${grouped.length} unique queries`
              : `${grouped.length} unique queries`}
          </span>
        )}
        {sorted.length > 0 && (
          <button
            type="button"
            className="aivis-csv-export-btn"
            onClick={() => exportCsv(sorted, clientName)}
            title="Export fan-out queries as CSV"
            style={{ marginLeft: 'auto' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
        )}
      </div>

      {error && <div className="aivis-auth-error" style={{ marginBottom: 16 }}>{error}</div>}

      <section className="aivis-dash-section">
        <div className="aivis-dash-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="aivis-foq-table">
              <thead>
                <tr>
                  <th className="aivis-foq-th-chevron" />
                  <SortHeader label="Fan-Out Query" sortKey="query" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Prompts" sortKey="prompts" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <th>Model</th>
                  <SortHeader label="Frequency" sortKey="totalFrequency" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className={anyExpanded ? 'aivis-foq-tbody--has-open' : ''}>
                {loading ? (
                  <SkeletonRows count={8} />
                ) : !selectedCampaignId ? (
                  <tr>
                    <td colSpan={5} className="aivis-dash-empty" style={{ padding: '32px 24px', textAlign: 'center' }}>
                      Select a client to view fan-out queries.
                    </td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="aivis-dash-empty" style={{ padding: '32px 24px', textAlign: 'center' }}>
                      {search
                        ? 'No queries match your search.'
                        : 'No fan-out queries recorded yet. They will appear after ChatGPT executions complete.'}
                    </td>
                  </tr>
                ) : (
                  paginated.flatMap((g) => {
                    const isMulti = g.prompts.length > 1;
                    const isOpen = expandedQueries.has(g.query);

                    const isFocused = isOpen;
                    const isDimmed = anyExpanded && !isFocused;

                    const mainRow = (
                      <tr
                        key={g.query}
                        className={[
                          'aivis-foq-tr--main',
                          isMulti ? 'aivis-foq-tr--expandable' : '',
                          isFocused ? 'aivis-foq-tr--focused' : '',
                          isDimmed ? 'aivis-foq-tr--dimmed' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={isMulti ? () => toggleExpand(g.query) : undefined}
                      >
                        {/* Chevron cell */}
                        <td className="aivis-foq-td-chevron">
                          {isMulti && <Chevron open={isOpen} />}
                        </td>

                        {/* Query text */}
                        <td className="aivis-foq-td-query">
                          <span className="aivis-foq-query-text">{g.query}</span>
                        </td>

                        {/* Prompt(s) */}
                        <td className="aivis-foq-td-prompt">
                          {isMulti ? (
                            <span className="aivis-foq-prompts-badge">
                              {g.prompts.length} prompts
                            </span>
                          ) : g.prompts[0]?.prompt_id ? (
                            <Link
                              to={`/ai-vis/prompts/${g.prompts[0].prompt_id}`}
                              className="aivis-foq-prompt-link"
                              title={g.prompts[0].prompt_text}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {g.prompts[0].prompt_text && g.prompts[0].prompt_text.length > 80
                                ? `${g.prompts[0].prompt_text.slice(0, 80)}…`
                                : (g.prompts[0].prompt_text || '—')}
                            </Link>
                          ) : (
                            <span className="aivis-text-muted">—</span>
                          )}
                        </td>

                        {/* Model(s) */}
                        <td className="aivis-foq-td-model">
                          {g.models.length > 0 ? (
                            <div className="aivis-foq-models">
                              {g.models.map((m) => (
                                <span key={m} className="aivis-resp-model-badge" title={m}>
                                  {formatModelName(m)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="aivis-prompts-muted">—</span>
                          )}
                        </td>

                        {/* Total frequency */}
                        <td className="aivis-foq-td-freq">
                          <FrequencyCell value={g.totalFrequency} maxValue={maxFreq} />
                        </td>
                      </tr>
                    );

                    if (!isMulti || !isOpen) return [mainRow];

                    const subRows = g.prompts.map((p, pi) => (
                      <tr key={`${g.query}-sub-${pi}`} className="aivis-foq-tr--subrow">
                        <td className="aivis-foq-td-chevron aivis-foq-td-subrow-indent" />
                        <td className="aivis-foq-td-query aivis-foq-td-subrow-indent">
                          {/* empty — query already shown in parent */}
                        </td>
                        <td className="aivis-foq-td-prompt">
                          {p.prompt_id ? (
                            <Link
                              to={`/ai-vis/prompts/${p.prompt_id}`}
                              className="aivis-foq-prompt-link"
                              title={p.prompt_text}
                            >
                              {p.prompt_text && p.prompt_text.length > 80
                                ? `${p.prompt_text.slice(0, 80)}…`
                                : (p.prompt_text || '—')}
                            </Link>
                          ) : (
                            <span className="aivis-text-muted">—</span>
                          )}
                        </td>
                        <td className="aivis-foq-td-model" />
                        <td className="aivis-foq-td-freq">
                          <FrequencyCell value={p.frequency} maxValue={maxFreq} />
                        </td>
                      </tr>
                    ));

                    return [mainRow, ...subRows];
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && sorted.length > 0 && (
            <PaginationBar
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              total={sorted.length}
              onPage={setPage}
              onPageSize={(s) => { setPageSize(s); setPage(1); }}
            />
          )}
        </div>
      </section>
    </div>
  );
}
